import { CmpsUser, MetricPerformanceData } from '../../../kit';
import { AuthService } from '../../auth/auth.service';
import { CardPerformanceDataContainer } from '../../insight/models/card-performance-data-container.model';
import { MetricThresholdBand } from '../../insight/models/metric-threshold-band.model';
import { epicDependencies } from '../../store';
import { PayloadAction } from '../../store.model';
import { PersonOfInterestModel } from '../../user/people-of-interest.user.model';
import { prepareMetricPerformanceData } from '../functions/metric-performance-data.function';
import { PerformanceCard } from '../models/performance-card.model';
import { PerformanceMetricData } from '../models/performance-metric-data.model';
import { PerformancePillar } from '../models/performance-pillar.model';
import { ActionType } from '../my-performance-card.action';
import { PerformanceCardState } from '../my-performance-card.state';

import { GetMetricPerformanceDataResultAction } from './metric-performance-data.action';

export const handleMetricPerformanceDataResult = (
    state: PerformanceCardState,
    action: PayloadAction<
        ActionType.MY_PERFORMANCE_CARD__GET_METRIC_PERFORMANCE_DATA_RESULT,
        GetMetricPerformanceDataResultAction
    >,
): PerformanceCardState => {
    const userData = state.userData[action.payload.userId];
    const metricPerformanceData = action.payload.metricPerformanceData;
    const usersWithAvatars = action.payload.poiUserData;
    const userIdsHaveOtherManagers = action.payload.userIdsHaveOtherManagers;
    const extendedMetrics = getAllMetrics(userData.performanceCard);
    const extendedMap = getExtendedPropsMap(extendedMetrics);

    const mergedItems: CardPerformanceDataContainer[] = buildMergedMetricItems(
        metricPerformanceData,
        extendedMap,
    );

    const preparedMetrics = mergedItems.map((item) =>
        prepareMetricPerformanceData(item, true, userIdsHaveOtherManagers),
    );

    const pillarsData = attachChildRowsToPillars(
        userData.pillarsData,
        preparedMetrics,
    );

    const users =
        state.users.length > 0
            ? state.users
            : mergeUniqueUsers(metricPerformanceData, usersWithAvatars);

    return {
        ...state,
        users,
        userData: {
            ...state.userData,
            [action.payload.userId]: {
                ...userData,
                pillarsData,
                metricPerformanceData: preparedMetrics,
            },
        },
    };
};

const getAllMetrics = (
    performanceCard: PerformanceCard,
): CardPerformanceDataContainer[] => {
    return performanceCard.pillars.flatMap((pillar) =>
        pillar.colourGroups.flatMap((group) => group.metrics ?? []),
    );
};

function getExtendedPropsMap(
    extendedMetrics: CardPerformanceDataContainer[],
): Map<
    number,
    {
        minValueMetric?: number;
        maxValueMetric?: number;
        thresholdBands?: MetricThresholdBand[];
        overallSuperGroupActual?: number;
    }
> {
    const map = new Map<
        number,
        {
            minValueMetric?: number;
            maxValueMetric?: number;
            thresholdBands?: MetricThresholdBand[];
            overallSuperGroupActual?: number;
        }
    >();

    for (const extMetric of extendedMetrics) {
        map.set(extMetric.metricID, {
            minValueMetric: extMetric.minValueMetric,
            maxValueMetric: extMetric.maxValueMetric,
            thresholdBands: extMetric.thresholdBands,
            overallSuperGroupActual: extMetric.overallSuperGroupActual,
        });
    }
    return map;
}

function buildMergedMetricItems(
    payload: PerformanceMetricData[],
    extendedMap: Map<
        number,
        {
            minValueMetric?: number;
            maxValueMetric?: number;
            thresholdBands?: MetricThresholdBand[];
            overallSuperGroupActual?: number;
        }
    >,
): CardPerformanceDataContainer[] {
    return payload.map((pm) => {
        const extended = extendedMap.get(pm.metricID);

        const eventDate =
            pm.eventDate instanceof Date
                ? pm.eventDate.toString()
                : pm.eventDate;

        const newMetricItem: CardPerformanceDataContainer = {
            primaryName: pm.userName,
            primaryId: pm.userId,
            metricID: pm.metricID,
            metricName: pm.metricName,
            actual: pm.actual,
            goal: pm.goal,
            previousActual: pm.previousActual,
            rankOrder: pm.rankOrder,
            target: pm.target,
            eventDate,
            secondaryName: pm.secondaryName,
            targetThresholdColour: pm.targetThresholdColour,

            viewType: pm.viewType,
            minValueMetric: extended?.minValueMetric,
            maxValueMetric: extended?.maxValueMetric,
            thresholdBands: extended?.thresholdBands,
            overallSuperGroupActual: extended?.overallSuperGroupActual,
        };

        return newMetricItem;
    });
}

function attachChildRowsToPillars(
    pillars: PerformancePillar[],
    allPrepared: MetricPerformanceData[],
): PerformancePillar[] {
    return pillars.map((pillar) => {
        if (pillar?.childPillars?.length > 0) {
            const updatedChildPillars = pillar.childPillars.map((child) => {
                const matchingRows = allPrepared
                    .filter((m) => m.metricId === child.metricId)
                    .sort((a, b) => {
                        let ret =
                            a.rankOrder === 0
                                ? a.actual - b.actual
                                : b.actual - a.actual;
                        if (ret === 0) {
                            ret = (a.user?.userName ?? '').localeCompare(
                                b.user?.userName ?? '',
                            );
                        }
                        return ret;
                    });
                return {
                    ...child,
                    childRows: matchingRows,
                    isExpandable: matchingRows?.length > 0,
                };
            });
            return { ...pillar, childPillars: updatedChildPillars };
        }
        return pillar;
    });
}

function mergeUniqueUsers(
    metricPerformanceData: PerformanceMetricData[],
    poiUserData: PersonOfInterestModel[],
): CmpsUser[] {
    const userMap: Map<number, CmpsUser> = new Map();
    const service = epicDependencies.injector.get<AuthService>(AuthService);
    const loggedInUserId = service.user.id;
    const loggedInUserName = `${service.user.firstName} ${service.user.lastName}`;

    for (const metric of metricPerformanceData) {
        userMap.set(metric.userId, {
            userId: metric.userId,
            userName: metric.userName,
            isLoggedIn: loggedInUserId === metric.userId,
            isFavourite: false,
        });
    }

    for (const user of poiUserData) {
        if (userMap.has(user.userId)) {
            const existingUser = userMap.get(user.userId);
            existingUser.isFavourite = user.isAvailable;
        }
    }

    if (!userMap.has(loggedInUserId)) {
        userMap.set(loggedInUserId, {
            userId: loggedInUserId,
            userName: loggedInUserName,
            isLoggedIn: true,
            isFavourite: false,
        });
    }

    const userList = Array.from(userMap.values());

    userList.sort((a, b) => {
        if (a.isLoggedIn === true) return -1;
        if (b.isLoggedIn === true) return 1;

        if (a.isFavourite === true && b.isFavourite === false) return -1;
        if (a.isFavourite === false && b.isFavourite === true) return 1;

        return a.userName.localeCompare(b.userName);
    });

    return userList;
}
